import type { UpgradeConfig } from '@/api/models/UpgradeConfig'
import type { UpgradeBrowseItem } from '@/api/models/UpgradeBrowseItem'

/**
 * Maximum price range multiplier for browse items
 * Target items can be up to 1.5x the minimum price
 */
const PRICE_RANGE_MULTIPLIER = 1.5

/**
 * House edge applied to upgrade win chances.
 */
export const UPGRADE_HOUSE_EDGE = 0.1

/**
 * Legacy maximum chance enforced by the upgrade backend.
 */
export const UPGRADE_MAX_WIN_CHANCE = 80

/**
 * Keeps the zero-edge promotional UI available while preventing it from being rendered.
 */
export const SHOW_ZERO_EDGE_MARKETING = false

/**
 * Maximum number of items that can be selected for upgrade
 */
export const MAX_SELECTED_ITEMS = 3

/**
 * Calculates the minimum price for target items based on input value and multiplier
 * 
 * @param inputValue - Total value of selected input items in cents
 * @param multiplier - Upgrade multiplier (e.g., 2.0 for 2x)
 * @returns Minimum target item price in cents, rounded up
 * @throws {Error} If inputValue or multiplier are invalid
 * 
 * @example
 * calculateMinPrice(100, 2.0) // returns 200
 * calculateMinPrice(150, 1.5) // returns 225
 */
export function calculateMinPrice(inputValue: number, multiplier: number): number {
  if (!Number.isFinite(inputValue) || inputValue < 0) {
    throw new Error('Input value must be a non-negative finite number')
  }
  
  if (!Number.isFinite(multiplier) || multiplier <= 0) {
    throw new Error('Multiplier must be a positive finite number')
  }

  return Math.ceil(inputValue * multiplier)
}

/**
 * Calculates the maximum price for target items based on minimum price
 * Target items can be up to 1.5x the minimum price to provide a range
 * 
 * @param minPrice - Minimum target item price in cents
 * @returns Maximum target item price in cents, rounded down
 * @throws {Error} If minPrice is invalid
 * 
 * @example
 * calculateMaxPrice(200) // returns 300 (200 * 1.5)
 * calculateMaxPrice(333) // returns 499 (333 * 1.5 rounded down)
 */
export function calculateMaxPrice(minPrice: number): number {
  if (!Number.isFinite(minPrice) || minPrice < 0) {
    throw new Error('Minimum price must be a non-negative finite number')
  }

  return Math.floor(minPrice * PRICE_RANGE_MULTIPLIER)
}

/**
 * Calculates the upgrade win chance after applying the configured house edge.
 *
 * @param inputValue - Total value of selected input items in cents
 * @param targetPrice - Target item price in cents
 * @returns Win chance as a percentage between 0 and the configured maximum
 */
export function calculateWinChance(inputValue: number, targetPrice: number): number {
  if (!Number.isFinite(inputValue) || inputValue < 0) {
    throw new Error('Input value must be a non-negative finite number')
  }

  if (!Number.isFinite(targetPrice) || targetPrice <= 0) {
    throw new Error('Target price must be a positive finite number')
  }

  const chance = (inputValue / targetPrice) * (1 - UPGRADE_HOUSE_EDGE) * 100
  return Math.min(Math.max(chance, 0), UPGRADE_MAX_WIN_CHANCE)
}

/**
 * Validates if a target item's price falls within the acceptable range
 * 
 * @param targetItem - The browse item to validate
 * @param minPrice - Minimum acceptable price in cents
 * @param maxPrice - Maximum acceptable price in cents
 * @returns True if item price is within range, false otherwise
 * 
 * @example
 * validateTargetItem({ price: 250 }, 200, 300) // returns true
 * validateTargetItem({ price: 350 }, 200, 300) // returns false
 */
export function validateTargetItem(
  targetItem: Pick<UpgradeBrowseItem, 'price'>,
  minPrice: number,
  maxPrice: number,
): boolean {
  const itemPrice = targetItem.price ?? 0
  return itemPrice >= minPrice && itemPrice <= maxPrice
}

/**
 * Validation result for upgrade selection
 */
export interface ValidationResult {
  valid: boolean
  error?: string
}

/**
 * Validates the complete upgrade selection before attempting an upgrade
 * Checks item selection, multiplier range, and target item selection
 * 
 * @param selectedItemIds - Array of selected inventory item IDs
 * @param multiplier - Selected multiplier value
 * @param targetItemId - Selected target item ID
 * @param config - Upgrade configuration from server
 * @returns Validation result with error message if invalid
 * 
 * @example
 * validateSelection(['item1'], 2.0, 'target1', config)
 * // returns { valid: true }
 * 
 * validateSelection([], 2.0, 'target1', config)
 * // returns { valid: false, error: 'No items selected' }
 */
export function validateSelection(
  selectedItemIds: string[],
  multiplier: number | null,
  targetItemId: string | null,
  config: UpgradeConfig | null,
): ValidationResult {
  if (!selectedItemIds || selectedItemIds.length === 0) {
    return { valid: false, error: 'No items selected' }
  }

  if (selectedItemIds.length > MAX_SELECTED_ITEMS) {
    return { valid: false, error: `Maximum ${MAX_SELECTED_ITEMS} items allowed` }
  }

  if (!multiplier || !Number.isFinite(multiplier)) {
    return { valid: false, error: 'Multiplier not selected' }
  }

  if (!config) {
    return { valid: false, error: 'Configuration not loaded' }
  }

  if (multiplier < config.minMultiplier || multiplier > config.maxMultiplier) {
    return {
      valid: false,
      error: `Multiplier must be between ${config.minMultiplier}x and ${config.maxMultiplier}x`,
    }
  }

  if (!targetItemId) {
    return { valid: false, error: 'Target item not selected' }
  }

  return { valid: true }
}

/**
 * Formats a price value from cents to dollars with 2 decimal places
 * 
 * @param cents - Price in cents
 * @returns Formatted price string with dollar sign
 * 
 * @example
 * formatPrice(12345) // returns "$123.45"
 * formatPrice(100) // returns "$1.00"
 */
export function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`
}

/**
 * Formats a multiplier value for display
 * 
 * @param value - Multiplier value
 * @returns Formatted multiplier string with 'x' suffix
 * 
 * @example
 * formatMultiplier(2) // returns "2.0x"
 * formatMultiplier(1.5) // returns "1.5x"
 */
export function formatMultiplier(value: number): string {
  return `${value.toFixed(1)}x`
}

/**
 * Formats a win chance percentage for display
 * 
 * @param chance - Win chance as a percentage (0-100)
 * @returns Formatted percentage string
 * 
 * @example
 * formatWinChance(45.67) // returns "45.67%"
 * formatWinChance(100) // returns "100.00%"
 */
export function formatWinChance(chance: number): string {
  return `${chance.toFixed(2)}%`
}

